import { ReactNode, useEffect, useRef, useState } from "react";

import { BrowserAuthErrorCodes, ClientAuthErrorCodes } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import appConfig from "@config/app";
import * as Sentry from "@sentry/react";
import { BotInfo, BrowserInfo, NodeInfo, OperatingSystem } from "detect-browser";
import { Button } from "emoreg";
import { Title } from "emoreg/atoms/Title";
import { Typography } from "emoreg/atoms/Typography";
import { color, font, radius, spaces } from "emoreg/const";
import { Flex } from "emoreg/layouts";
import { XimiMigrationWarningAtConnection } from "hassibot/component/maintenance-mode/XimiMigrationWarning";
import { useModalApi } from "hassibot/component/modal";
import { AppUpdateModalContent } from "hassibot/component/modals/app-update-modal/app-update-modal-content";
import { OPEN_UPGRADE_MODAL } from "hassibot/services_v2";
import {
  getMsalErrorMessage,
  isMobile,
  isMsalError,
  loginWithPopup,
  loginWithRedirect,
} from "hassibot/services_v2/login/msal";
import { SUPPORT_EMAIL } from "hassibot/util/pricing/constants";
import { rem } from "polished";
import styled from "styled-components";
import { Emoji } from "styles/atoms";

/**
 *  In this file you will find the full-screen components that are not Hassibot.
 *  Things like 404, login, error, ...
 */
const StandaloneAppFrameStyle = styled.div`
  height: 100vh;
  margin: auto;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
const WrapperGoogleConnect = styled.div`
  flex: 0 1;
  height: ${rem(430)};
  border: 0.5px solid ${color.grey.base};
  border-radius: ${radius.large};
  padding: ${spaces[32]};
`;
const TypographyHighlight = styled.span`
  background: "#eee";
  padding: ${rem(3)} ${rem(6)};
  font-weight: ${font.weight.semiBold};
`;
const XimiMigrationWarning = styled.div`
  text-align: left;
  justify-content: left;
  align-items: left;
  margin-top: 16px;
  max-width: ${props => props.maxWidth};
  ul {
    list-style-type: disc;
    margin-block-start: 0px;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 40px;
    margin-bottom: 0;
  }
`;

type StandalonAppFrameProps = {
  title: string;
  children?: ReactNode;
};

const StandaloneAppFrame = (props: StandalonAppFrameProps) => {
  const wrapperGoogleConnectRef = useRef<HTMLDivElement>(null);
  const [wrapperGoogleConnectWidth, setWrapperGoogleConnectWidth] = useState<string>();

  useEffect(() => {
    setWrapperGoogleConnectWidth(
      `${wrapperGoogleConnectRef?.current?.getBoundingClientRect().width}px`
    );
  }, []);

  return (
    <StandaloneAppFrameStyle>
      <WrapperGoogleConnect ref={wrapperGoogleConnectRef}>
        <Flex gap={16} direction={"column"}>
          {props.title && <Title type="h1">{props.title}</Title>}
          {props.children}
        </Flex>
      </WrapperGoogleConnect>
      {appConfig.IS_XIMI_MIGRATION_WARNING && wrapperGoogleConnectWidth && (
        <XimiMigrationWarning maxWidth={wrapperGoogleConnectWidth}>
          <XimiMigrationWarningAtConnection />
        </XimiMigrationWarning>
      )}
    </StandaloneAppFrameStyle>
  );
};

export const The404 = () => (
  <StandaloneAppFrame title={"Ooops, page non trouvé..."}>
    <Flex direction={"column"} gap={12}>
      <Typography>
        À transmettre à <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{" "}
        <TypographyHighlight>{window.location.pathname}</TypographyHighlight>
      </Typography>
      <Typography>
        <a href="/">Retour à l'application</a>
      </Typography>
    </Flex>
  </StandaloneAppFrame>
);

export const UnsupportedBrowser = () => (
  <StandaloneAppFrame title={"Votre navigateur n'est pas pris en charge !"}>
    <Typography>
      Ecrivez à <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> pour plus d'informations
    </Typography>
  </StandaloneAppFrame>
);

export const OldChromeBrowser = ({ browserOs }: { browserOs: OperatingSystem }) => {
  const text = "Mettez à jour votre version de Chrome pour accéder à Hassibot.";
  return (
    <StandaloneAppFrame title={"Votre navigateur est trop ancien !"}>
      <Typography>
        {browserOs === "Android OS" ? (
          <a href="https://play.google.com/store/apps/details?id=com.android.chrome&hl=fr">
            {text}
          </a>
        ) : (
          text
        )}
      </Typography>
    </StandaloneAppFrame>
  );
};

export const OldSafariBrowser = ({ browserOs }: { browserOs: OperatingSystem }) => {
  let toUpdate = "de Safari";
  if (browserOs === "iOS") {
    toUpdate = "d'iOS";
  } else if (browserOs === "Mac OS") {
    toUpdate = "de macOS";
  }
  return (
    <StandaloneAppFrame title={"Votre navigateur est trop ancien !"}>
      <Typography>Mettez à jour votre version {toUpdate} pour accéder à Hassibot.</Typography>
    </StandaloneAppFrame>
  );
};

export const WrongBrowserOnMobile = ({
  infos,
  platform,
}: {
  platform: OperatingSystem;
  infos: BrowserInfo | BotInfo | NodeInfo | null;
}) => (
  <StandaloneAppFrame title={"Mauvais navigateur !"}>
    {platform === "Android OS" ? (
      <Typography>
        Utilisez{" "}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://play.google.com/store/apps/details?id=com.android.chrome"
        >
          Google Chrome
        </a>{" "}
        pour accéder à Hassibot sur Android.
      </Typography>
    ) : (
      <Typography>
        Utilisez{" "}
        <a target="_blank" rel="noopener noreferrer" href="https://www.apple.com/fr/safari/">
          Safari
        </a>{" "}
        pour accéder à Hassibot sur iOS.
      </Typography>
    )}
    <details style={{ opacity: 0.2 }}>{JSON.stringify(infos)}</details>
  </StandaloneAppFrame>
);

export const BadRequest = () => (
  <StandaloneAppFrame title={"Ooops, mauvaise URL"}>
    <Typography>
      Pas besoin de transmettre à la tech, l'URL saisie n'est pas correcte <Emoji name="bomb" />.
    </Typography>
  </StandaloneAppFrame>
);

export const HoustonWeveGotAProblem = ({ eventId }: { eventId?: string }) => {
  const subject = encodeURIComponent("Erreur Hassibot");
  const body = eventId ? `&body=${encodeURIComponent(`Référence de l'erreur : ${eventId}`)}` : "";
  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${subject}${body}`;
  return (
    <StandaloneAppFrame title={"Ooops, on a eu un problème..."}>
      <Typography>
        Veuillez réessayer, si le problème persiste, contactez{" "}
        <a href={mailtoHref}>{SUPPORT_EMAIL}</a>
      </Typography>
      {eventId && <Typography>Référence : {eventId}</Typography>}
      <Button size="large" onClick={() => window.location.reload()}>
        Recharger la page
      </Button>
    </StandaloneAppFrame>
  );
};

type LoginErrorState = {
  message: string;
  // false for user-actionable errors (network, popups…), where asking to
  // forward the message to support would be contradictory.
  shouldContactSupport: boolean;
};

const LoginError = ({ error, onRetry }: { error: LoginErrorState; onRetry: () => void }) => (
  <StandaloneAppFrame title={"Problème de connexion"}>
    <Flex direction={"column"} gap={12}>
      {error.shouldContactSupport ? (
        <Typography>
          Veuillez transmettre ce message à <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>:{" "}
          {error.message}
        </Typography>
      ) : (
        <Typography>{error.message}</Typography>
      )}
      <Button size="large" onClick={onRetry}>
        Réessayer
      </Button>
    </Flex>
  </StandaloneAppFrame>
);

export const Loading = () => <StandaloneAppFrame title={"Chargement en cours..."} />;

export const LoginStandaloneApp = () => {
  const { instance, inProgress } = useMsal();
  const [loginError, setLoginError] = useState<LoginErrorState | null>(null);

  const handleLogin = async () => {
    try {
      setLoginError(null);
      if (isMobile()) {
        await loginWithRedirect(instance);
      } else {
        await loginWithPopup(instance);
      }
    } catch (error: unknown) {
      console.error("Login failed", error);

      // Handle specific MSAL errors
      if (isMsalError(error, BrowserAuthErrorCodes.popupWindowError)) {
        setLoginError({
          message: "Popup bloqué. Veuillez autoriser les popups dans votre navigateur.",
          shouldContactSupport: false,
        });
      } else if (isMsalError(error, BrowserAuthErrorCodes.userCancelled)) {
        return;
      } else if (isMsalError(error, BrowserAuthErrorCodes.hashEmptyError)) {
        // Mobile navigation can consume the auth response hash fragment
        // before MSAL processes it — transient, retrying usually works.
        setLoginError({
          message: "La connexion n'a pas abouti. Veuillez réessayer.",
          shouldContactSupport: false,
        });
      } else if (
        isMsalError(error, ClientAuthErrorCodes.networkError) ||
        isMsalError(error, BrowserAuthErrorCodes.noNetworkConnectivity) ||
        isMsalError(error, BrowserAuthErrorCodes.postRequestFailed)
      ) {
        // post_request_failed: POST to the AAD token endpoint blocked client-side
        // (proxy, antivirus, adblocker) — infra condition, not a bug to capture.
        setLoginError({
          message:
            "Impossible de contacter le serveur d'authentification. " +
            "Vérifiez votre connexion internet ou votre proxy.",
          shouldContactSupport: false,
        });
      } else if (isMsalError(error, BrowserAuthErrorCodes.blockNestedPopups)) {
        // MSAL rejects loginPopup when the app already runs inside an MSAL
        // popup: fall back to the redirect flow, navigating this same window.
        try {
          await loginWithRedirect(instance);
        } catch {
          setLoginError({
            message:
              "La connexion ne peut pas s'effectuer dans cette fenêtre. " +
              "Veuillez vous reconnecter depuis l'onglet principal de Hassibot.",
            shouldContactSupport: false,
          });
        }
      } else {
        setLoginError({ message: getMsalErrorMessage(error), shouldContactSupport: true });
        Sentry.captureException(error);
      }
    }
  };

  if (loginError) {
    return <LoginError error={loginError} onRetry={() => setLoginError(null)} />;
  }

  const isInLoginProcess = inProgress === "login";

  return (
    <StandaloneAppFrame title={"Connexion à Hassibot"}>
      <Button size="large" onClick={handleLogin} disabled={isInLoginProcess}>
        {isInLoginProcess ? "Connexion en cours..." : "Se connecter"}
      </Button>
    </StandaloneAppFrame>
  );
};

export const AppUpdateModal = () => {
  const modalApi = useModalApi();

  useEffect(() => {
    const handleUpgradeRequired = (event: MessageEvent) => {
      if (event.data?.type === OPEN_UPGRADE_MODAL) {
        modalApi.showModal<void>(({ resolver, hideModal }) => (
          <AppUpdateModalContent resolver={resolver} hideModal={hideModal} />
        ));
      }
    };

    window.addEventListener("message", handleUpgradeRequired);
    return () => window.removeEventListener("message", handleUpgradeRequired);
  }, [modalApi]);

  return null;
};
